import React, { useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { Message, Confirm, Modal, SearchableSelect } from './index';

const ManualTriggerPanel = ({ style, embedded = false, onBusyChange }) => {
  const [manualIssueKey, setManualIssueKey] = useState('');
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [fields, setFields] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [triggerMode, setTriggerMode] = useState('issue');
  const [queryProjectKeys, setQueryProjectKeys] = useState('');
  const [queryConditions, setQueryConditions] = useState([]);
  const [queryConditionLogic, setQueryConditionLogic] = useState('and');
  const [queryMaxResults, setQueryMaxResults] = useState(100);
  const [queryPageSize, setQueryPageSize] = useState(20);
  const [newQueryCondition, setNewQueryCondition] = useState({ field: '', operator: 'equals', value: '' });
  const [queryPreview, setQueryPreview] = useState(null);
  const [queryPreviewLoading, setQueryPreviewLoading] = useState(false);
  const queryPreviewVersionRef = useRef(0);
  const [showQueryTaskModal, setShowQueryTaskModal] = useState(false);
  const [queryTaskForm, setQueryTaskForm] = useState({
    name: '',
    schedule_type: 'hourly',
    interval_minutes: 60,
    schedule_time: '09:00',
    week_day: 0,
    dedupe_hours: 24
  });
  const [confirmState, setConfirmState] = useState({ isOpen: false, message: '', onConfirm: null });
  const [alertState, setAlertState] = useState({ isOpen: false, message: '' });

  useEffect(() => {
    onBusyChange?.(triggerLoading || queryPreviewLoading || confirmState.isOpen || showQueryTaskModal);
  }, [onBusyChange, triggerLoading, queryPreviewLoading, confirmState.isOpen, showQueryTaskModal]);

  useEffect(() => {
    const fetchTriggerData = async () => {
      try {
        const [groupsResponse, fieldsResponse] = await Promise.all([
          api.getGroups(),
          api.getFields()
        ]);
        if (Array.isArray(groupsResponse.data)) {
          setGroups(groupsResponse.data);
        }
        if (Array.isArray(fieldsResponse.data)) {
          setFields(fieldsResponse.data.filter(field => field.is_active !== false));
        }
      } catch (err) {
        console.error('获取手动触发配置失败:', err);
      }
    };
    fetchTriggerData();
  }, []);

  const queryOperatorOptions = [
    { value: 'equals', label: '等于' },
    { value: 'not_equals', label: '不等于' },
    { value: 'contains', label: '包含' },
    { value: 'not_contains', label: '不包含' },
    { value: 'is_empty', label: '为空' },
    { value: 'is_not_empty', label: '不为空' },
    { value: 'gt', label: '大于' },
    { value: 'lt', label: '小于' },
    { value: 'gte', label: '大于等于' },
    { value: 'lte', label: '小于等于' }
  ];

  const selectedQueryField = fields.find(field => field.field_id === newQueryCondition.field);
  const selectedQueryFieldOptions = selectedQueryField?.options || [];
  const queryConditionNeedsValue = !['is_empty', 'is_not_empty'].includes(newQueryCondition.operator);
  const queryConditionSupportsMultipleValues = ['contains', 'not_contains'].includes(newQueryCondition.operator) && selectedQueryFieldOptions.length > 0;

  const parseQueryProjectKeys = () => queryProjectKeys
    .split(/[,\s]+/)
    .map(key => key.trim())
    .filter(Boolean);

  const buildEffectiveQueryConditions = () => {
    const projectKeys = parseQueryProjectKeys();
    const projectCondition = projectKeys.length > 1
      ? { field: 'project', operator: 'in', value: projectKeys }
      : { field: 'project', operator: 'equals', value: projectKeys[0] };
    return [projectCondition, ...queryConditions];
  };

  useEffect(() => {
    if (!queryPreview?.expires_at) return undefined;
    const timeout = setTimeout(() => {
      queryPreviewVersionRef.current += 1;
      setQueryPreview(null);
      setAlertState({ isOpen: true, message: '查询预览已过期，请重新预览' });
    }, Math.max(0, Date.parse(queryPreview.expires_at) - Date.now()));
    return () => clearTimeout(timeout);
  }, [queryPreview]);

  const buildQueryPayload = () => ({
    group_id: parseInt(selectedGroup, 10),
    conditions: buildEffectiveQueryConditions(),
    condition_logic: queryConditionLogic,
    max_results: parseInt(queryMaxResults, 10) || 100,
    page_size: parseInt(queryPageSize, 10) || 20
  });

  const invalidateQueryPreview = () => {
    queryPreviewVersionRef.current += 1;
    setQueryPreview(null);
  };

  const validateQueryTrigger = () => {
    if (!selectedGroup) {
      setAlertState({ isOpen: true, message: '请选择要触发的规则组' });
      return false;
    }
    if (parseQueryProjectKeys().length === 0) {
      setAlertState({ isOpen: true, message: '请输入项目 Key' });
      return false;
    }
    if (queryConditions.length === 0) {
      setAlertState({ isOpen: true, message: '请至少添加一个查询条件' });
      return false;
    }
    const maxResults = Number(queryMaxResults);
    const pageSize = Number(queryPageSize);
    if (!Number.isInteger(maxResults) || maxResults < 1 || maxResults > 10000) {
      setAlertState({ isOpen: true, type: 'error', message: '最大数量必须为 1–10000 的整数' });
      return false;
    }
    if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 50) {
      setAlertState({ isOpen: true, type: 'error', message: '单页数量必须为 1–50 的整数' });
      return false;
    }
    if (pageSize > maxResults) {
      setAlertState({ isOpen: true, message: '单页数量不能大于最大数量' });
      return false;
    }
    return true;
  };

  const handleManualTrigger = async () => {
    if (!manualIssueKey.trim()) {
      setAlertState({ isOpen: true, message: '请输入工单 Key' });
      return;
    }
    if (!selectedGroup) {
      setAlertState({ isOpen: true, message: '请选择要触发的规则组' });
      return;
    }
    setTriggerLoading(true);
    try {
      const response = await api.triggerManual({
        issue_key: manualIssueKey.trim(),
        group_id: parseInt(selectedGroup, 10)
      });
      setAlertState({ isOpen: true, message: '已触发处理: ' + response.data.message });
      setManualIssueKey('');
      setSelectedGroup('');
    } catch (err) {
      setAlertState({ isOpen: true, message: '触发失败: ' + (err.response?.data?.detail || err.message) });
    } finally {
      setTriggerLoading(false);
    }
  };

  const addQueryCondition = () => {
    if (!newQueryCondition.field) {
      setAlertState({ isOpen: true, message: '请选择查询字段' });
      return;
    }
    if (queryConditionNeedsValue && Array.isArray(newQueryCondition.value) && newQueryCondition.value.length === 0) {
      setAlertState({ isOpen: true, message: '请选择查询条件值' });
      return;
    }
    if (queryConditionNeedsValue && !Array.isArray(newQueryCondition.value) && !String(newQueryCondition.value || '').trim()) {
      setAlertState({ isOpen: true, message: '请输入查询条件值' });
      return;
    }
    setQueryConditions(prev => [...prev, { ...newQueryCondition }]);
    setNewQueryCondition({ field: '', operator: 'equals', value: '' });
    invalidateQueryPreview();
  };

  const removeQueryCondition = (index) => {
    setQueryConditions(prev => prev.filter((_, i) => i !== index));
    invalidateQueryPreview();
  };

  const handlePreviewQuery = async () => {
    if (!validateQueryTrigger()) return;
    const previewVersion = queryPreviewVersionRef.current;
    setQueryPreviewLoading(true);
    try {
      const response = await api.previewQueryTrigger(buildQueryPayload());
      if (queryPreviewVersionRef.current === previewVersion) {
        setQueryPreview(response.data);
      }
    } catch (err) {
      if (queryPreviewVersionRef.current === previewVersion) {
        setAlertState({ isOpen: true, message: '预览失败: ' + (err.response?.data?.detail || err.message) });
      }
    } finally {
      setQueryPreviewLoading(false);
    }
  };

  const handleTriggerQueryNow = async () => {
    if (!validateQueryTrigger()) return;
    if (!queryPreview) {
      setAlertState({ isOpen: true, message: '请先预览查询结果' });
      return;
    }
    if (!queryPreview.preview_id || Date.parse(queryPreview.expires_at) <= Date.now()) {
      invalidateQueryPreview();
      setAlertState({ isOpen: true, message: '预览已过期，请重新预览' });
      return;
    }
    setConfirmState({
      isOpen: true,
      message: `确定要触发本次预览的 ${queryPreview.issues?.length || 0} 个工单吗？`,
      onConfirm: async () => {
        if (triggerLoading) return;
        setTriggerLoading(true);
        try {
          const response = await api.triggerQueryNow({ ...buildQueryPayload(), preview_id: queryPreview.preview_id });
          setAlertState({ isOpen: true, message: `已创建 ${response.data.triggered || 0} 个触发任务` });
          setQueryPreview(null);
          setConfirmState(prev => ({ ...prev, isOpen: false }));
        } catch (err) {
          if ([400, 409, 410].includes(err.response?.status)) {
            invalidateQueryPreview();
            setConfirmState(prev => ({ ...prev, isOpen: false }));
          }
          setAlertState({ isOpen: true, message: '触发失败: ' + (err.response?.data?.detail || err.message) });
        } finally {
          setTriggerLoading(false);
        }
      }
    });
  };

  const handleCreateQueryTask = async () => {
    if (!validateQueryTrigger()) return;
    if (!queryPreview) {
      setAlertState({ isOpen: true, message: '请先预览查询结果' });
      return;
    }
    if (!queryTaskForm.name.trim()) {
      setAlertState({ isOpen: true, message: '请输入定时任务名称' });
      return;
    }
    try {
      await api.createQueryTriggerTask({
        ...buildQueryPayload(),
        ...queryTaskForm,
        interval_minutes: parseInt(queryTaskForm.interval_minutes, 10) || 60,
        week_day: parseInt(queryTaskForm.week_day, 10) || 0,
        dedupe_hours: parseInt(queryTaskForm.dedupe_hours, 10) || 24
      });
      setAlertState({ isOpen: true, message: '定时触发任务已创建' });
      setQueryTaskForm(prev => ({ ...prev, name: '' }));
      setShowQueryTaskModal(false);
    } catch (err) {
      setAlertState({ isOpen: true, message: '创建失败: ' + (err.response?.data?.detail || err.message) });
    }
  };

  const openQueryTaskModal = () => {
    if (!validateQueryTrigger()) return;
    if (!queryPreview) {
      setAlertState({ isOpen: true, message: '请先预览查询结果' });
      return;
    }
    setShowQueryTaskModal(true);
  };

  const getFieldLabel = (fieldId) => fields.find(field => field.field_id === fieldId)?.display_name || fieldId;
  const getOperatorLabel = (operator) => queryOperatorOptions.find(option => option.value === operator)?.label || operator;

  return (
    <section className="manual-trigger-workbench" style={style}>
      {!embedded && <header className="manual-trigger-workbench-header">
        <div>
          <h2>手动触发自动化</h2>
          <p>输入 Jira 工单 Key，或按条件查询工单并触发指定规则组。</p>
        </div>
      </header>}
      <div className="manual-trigger-workbench-body">
      <div className="manual-trigger-mode-switch">
        <button className={`btn ${triggerMode === 'issue' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTriggerMode('issue')}>按工单 Key</button>
        <button className={`btn ${triggerMode === 'query' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setTriggerMode('query')}>按条件查询</button>
      </div>

      {triggerMode === 'issue' ? (
        <div className="manual-trigger-issue-form">
          <div className="form-group manual-trigger-issue-key">
            <label className="form-label">工单 Key</label>
            <input type="text" value={manualIssueKey} onChange={(e) => setManualIssueKey(e.target.value)} className="form-input" placeholder="输入 Jira 工单 Key，如 PROJ-123" />
          </div>
          <div className="form-group manual-trigger-group-select">
            <label className="form-label">规则组 *</label>
            <SearchableSelect value={selectedGroup} options={groups.map(group => ({ value: group.id, label: group.name }))} onChange={(val) => { setSelectedGroup(val); invalidateQueryPreview(); }} placeholder="选择规则组" />
          </div>
          <button className="btn btn-primary" onClick={handleManualTrigger} disabled={triggerLoading || groups.length === 0}>
            {triggerLoading ? '触发中...' : '触发自动化'}
          </button>
        </div>
      ) : (
        <div className="manual-trigger-query-flow">
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group" style={{ width: '220px', marginBottom: 0 }}>
              <label className="form-label">规则组 *</label>
              <SearchableSelect value={selectedGroup} options={groups.map(group => ({ value: group.id, label: group.name }))} onChange={(val) => { setSelectedGroup(val); invalidateQueryPreview(); }} placeholder="选择规则组" />
            </div>
            <div className="form-group" style={{ width: '220px', marginBottom: 0 }}>
              <label className="form-label">项目 Key *</label>
              <input type="text" value={queryProjectKeys} onChange={(e) => { setQueryProjectKeys(e.target.value); invalidateQueryPreview(); }} className="form-input" placeholder="如 PAY 或 PAY, OPS" />
            </div>
            <div className="form-group" style={{ width: '160px', marginBottom: 0 }}>
              <label className="form-label">条件关系</label>
              <SearchableSelect value={queryConditionLogic} options={[{ value: 'and', label: '全部满足' }, { value: 'or', label: '任一满足' }]} onChange={(val) => { setQueryConditionLogic(val); invalidateQueryPreview(); }} searchable={false} />
            </div>
            <div className="form-group" style={{ width: '140px', marginBottom: 0 }}>
              <label className="form-label">最大数量 <span className="text-muted">（最多 10000）</span></label>
              <input type="number" min="1" max="10000" value={queryMaxResults} onChange={(e) => { setQueryMaxResults(Number(e.target.value) > 10000 ? '10000' : e.target.value); invalidateQueryPreview(); }} className="form-input" />
            </div>
            <div className="form-group" style={{ width: '140px', marginBottom: 0 }}>
              <label className="form-label">单页数量 <span className="text-muted">（最多 50）</span></label>
              <input type="number" min="1" max="50" value={queryPageSize} onChange={(e) => { setQueryPageSize(Number(e.target.value) > 50 ? '50' : e.target.value); invalidateQueryPreview(); }} className="form-input" />
            </div>
          </div>

          {(parseQueryProjectKeys().length > 0 || queryConditions.length > 0) && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {parseQueryProjectKeys().length > 0 && <span className="badge badge-default">项目 {parseQueryProjectKeys().length > 1 ? '属于' : '等于'} {parseQueryProjectKeys().join(', ')}</span>}
              {queryConditions.map((condition, index) => (
                <span key={`${condition.field}-${index}`} className="badge badge-default" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                  {getFieldLabel(condition.field)} {getOperatorLabel(condition.operator)} {['is_empty', 'is_not_empty'].includes(condition.operator) ? '' : condition.value}
                  <button className="btn btn-ghost btn-sm" onClick={() => removeQueryCondition(index)} style={{ padding: '2px 6px' }}>x</button>
                </span>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '12px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-md)' }}>
            <div className="form-group" style={{ flex: 1, minWidth: '180px', marginBottom: 0 }}>
              <label className="form-label">字段</label>
              <SearchableSelect value={newQueryCondition.field} options={fields.map(field => ({ value: field.field_id, label: field.display_name || field.field_id }))} onChange={(val) => setNewQueryCondition(prev => ({ ...prev, field: val, value: '' }))} placeholder="选择字段" />
            </div>
            <div className="form-group" style={{ width: '160px', marginBottom: 0 }}>
              <label className="form-label">操作符</label>
              <SearchableSelect
                value={newQueryCondition.operator}
                options={queryOperatorOptions}
                onChange={(val) => setNewQueryCondition(prev => ({
                  ...prev,
                  operator: val,
                  value: ['is_empty', 'is_not_empty'].includes(val) ? '' : ['contains', 'not_contains'].includes(val) && selectedQueryFieldOptions.length > 0 ? [] : ''
                }))}
                searchable={false}
              />
            </div>
            {queryConditionNeedsValue && (
              <div className="form-group" style={{ flex: 1, minWidth: '180px', marginBottom: 0 }}>
                <label className="form-label">值</label>
                {selectedQueryFieldOptions.length > 0 ? (
                  <SearchableSelect
                    value={newQueryCondition.value}
                    options={selectedQueryFieldOptions.map(option => ({ value: option.value || option.id || option.name, label: option.label || option.value || option.name }))}
                    onChange={(val) => setNewQueryCondition(prev => ({ ...prev, value: val }))}
                    placeholder={queryConditionSupportsMultipleValues ? '选择一个或多个值' : '选择值'}
                    multiple={queryConditionSupportsMultipleValues}
                  />
                ) : (
                  <input type="text" value={newQueryCondition.value} onChange={(e) => setNewQueryCondition(prev => ({ ...prev, value: e.target.value }))} className="form-input" placeholder="输入查询值" />
                )}
              </div>
            )}
            <button className="btn btn-secondary" onClick={addQueryCondition}>添加条件</button>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button className="btn btn-secondary" onClick={handlePreviewQuery} disabled={queryPreviewLoading}>{queryPreviewLoading ? '预览中...' : '预览查询'}</button>
            <button className="btn btn-primary" onClick={handleTriggerQueryNow} disabled={triggerLoading || !queryPreview}>{triggerLoading ? '触发中...' : '立即触发匹配工单'}</button>
            <button className="btn btn-secondary" onClick={openQueryTaskModal} disabled={!queryPreview}>保存定时任务</button>
          </div>

          {queryPreview && (
            <div style={{ padding: '12px', background: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px' }}>JQL: {queryPreview.jql}</div>
              <div style={{ marginBottom: '8px' }}>
                已获取 {queryPreview.total || 0} 个工单，单页 {queryPreview.page_size || queryPageSize} 个{queryPreview.has_more ? '，还有更多匹配结果未拉取' : ''}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {(queryPreview.issues || []).map(issue => <span key={issue.key} className="badge badge-default">{issue.key}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      <Confirm
        isOpen={confirmState.isOpen}
        title="确认操作"
        message={confirmState.message}
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState({ ...confirmState, isOpen: false })}
        loading={triggerLoading}
        disabled={triggerLoading}
        loadingText="创建中..."
      />
      {alertState.isOpen && <Message type={alertState.type || 'info'} message={alertState.message} onClose={() => setAlertState({ ...alertState, isOpen: false })} />}
      <Modal
        isOpen={showQueryTaskModal}
        title="保存定时任务"
        onClose={() => setShowQueryTaskModal(false)}
        size="large"
        buttons={[
          { label: '取消', onClick: () => setShowQueryTaskModal(false), variant: 'default' },
          { label: '保存', onClick: handleCreateQueryTask, variant: 'primary' }
        ]}
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">任务名</label>
            <input className="form-input" value={queryTaskForm.name} onChange={(e) => setQueryTaskForm(prev => ({ ...prev, name: e.target.value }))} placeholder="例如：每日检查待分配工单" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">频率</label>
              <SearchableSelect value={queryTaskForm.schedule_type} options={[{ value: 'hourly', label: '每小时' }, { value: 'daily', label: '每天' }, { value: 'weekly', label: '每周' }]} onChange={(val) => setQueryTaskForm(prev => ({ ...prev, schedule_type: val }))} searchable={false} />
            </div>
            {queryTaskForm.schedule_type === 'hourly' ? (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">间隔分钟</label>
                <input className="form-input" type="number" min="1" value={queryTaskForm.interval_minutes} onChange={(e) => setQueryTaskForm(prev => ({ ...prev, interval_minutes: e.target.value }))} />
              </div>
            ) : (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">时间</label>
                <input className="form-input" type="time" value={queryTaskForm.schedule_time} onChange={(e) => setQueryTaskForm(prev => ({ ...prev, schedule_time: e.target.value }))} />
              </div>
            )}
            {queryTaskForm.schedule_type === 'weekly' && (
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">星期</label>
                <SearchableSelect value={queryTaskForm.week_day} options={[{ value: 0, label: '周一' }, { value: 1, label: '周二' }, { value: 2, label: '周三' }, { value: 3, label: '周四' }, { value: 4, label: '周五' }, { value: 5, label: '周六' }, { value: 6, label: '周日' }]} onChange={(val) => setQueryTaskForm(prev => ({ ...prev, week_day: val }))} searchable={false} />
              </div>
            )}
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">去重小时</label>
              <input className="form-input" type="number" min="0" value={queryTaskForm.dedupe_hours} onChange={(e) => setQueryTaskForm(prev => ({ ...prev, dedupe_hours: e.target.value }))} />
            </div>
          </div>
        </div>
      </Modal>
      </div>
    </section>
  );
};

export default ManualTriggerPanel;
